import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, LogOut, User, Users, Bell } from "lucide-react";
import { getPageInfo } from "../constants/pageInfo";
import { getRequest } from "../apiWrapper";

const Sidebar = ({
  userRole,
  onLogout,
  teacherName = "未設定",
  schoolName = "教室",
  appName = "report",
}) => {
  const pageInfo = getPageInfo(appName);
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [menuOpen, setMenuOpen] = useState(window.innerWidth >= 1024);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const nowDesktop = window.innerWidth >= 1024;
      setIsDesktop(nowDesktop);
      setMenuOpen(nowDesktop);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 講師未読件数取得
  const fetchUnread = async () => {
    if (userRole === "teacher") {
      getRequest("notifications/list?includeRead=false")
        .then(data => setUnreadCount(data.length))
        .catch(err => console.error("未読通知取得エラー:", err));
    }
  };

  useEffect(() => {
    if (userRole === "teacher") {
      fetchUnread();
      // カスタムイベントリスナーをセット
      window.addEventListener("notificationRead", fetchUnread);
      return () => {
        window.removeEventListener("notificationRead", fetchUnread);
      };
    }
  }, [userRole]);

  const menuItems = pageInfo
    .filter(page => page.showInSidebar && page.visibleTo?.includes(userRole))
    .map(page => ({ path: page.path, title: page.title, icon: page.icon }));

  const getActivePath = () =>
    menuItems.reduce((acc, item) => {
      if (
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + "/")
      ) {
        return item.path.length > acc.length ? item.path : acc;
      }
      return acc;
    }, "");

  const activePath = getActivePath();

  return (
    <div className="relative">
      {/* ヘッダー */}
      <div className="bg-white flex items-center justify-between px-4 py-3 fixed top-0 left-0 w-full z-50 shadow-none">
        {!isDesktop && (
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-gray-700 p-2 rounded-md hover:bg-gray-200"
          >
            <Menu size={24} />
          </button>
        )}
        <h1 className="text-lg font-medium text-gray-800">
          {userRole === "admin" ? schoolName : `${teacherName} 先生`}
        </h1>
        <div className="flex items-center gap-4">
          {appName === "report" && userRole === "teacher" && (
            <button
              onClick={() => window.location.href = "/notifications"}
              className="relative text-gray-700 hover:text-blue-600 transition"
            >
              <Bell size={24} />
              {unreadCount > 0 && (
                <span className="absolute top-1 left-3 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          )}
          <button
            onClick={onLogout}
            className="text-gray-500 hover:text-red-500 transition"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>

      {/* オーバーレイ（モバイル時） */}
      {!isDesktop && menuOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* サイドバー */}
      <div
        className={`fixed top-0 left-0 h-full bg-white w-64 z-40 shadow-md transform transition-transform duration-300 ease-in-out ${menuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700">
            {userRole === "admin" ? schoolName : `${teacherName} 先生`}
          </h2>
        </div>
        <nav className="flex flex-col p-4 pb-20">
          {menuItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center py-3 px-4 rounded-md transition duration-200 ${activePath === item.path
                ? "bg-blue-500 text-white"
                : "hover:bg-gray-100 text-gray-700"
                }`}
              onClick={() => {
                if (!isDesktop) setMenuOpen(false);
              }}
            >
              {item.icon}
              <span className="ml-3">{item.title}</span>
            </Link>
          ))}
        </nav>
        <button
          onClick={onLogout}
          className="absolute bottom-4 left-0 w-full px-4 py-3 flex items-center justify-center text-red-500 font-medium rounded-t-lg hover:bg-red-100 transition"
        >
          <LogOut size={18} className="mr-2" />
          ログアウト
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
